/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

export const GrowBusinessNationallyBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      Every founder eventually hits the same ceiling: their city is tapped, referrals plateau,
      and the local Google Business Profile stops moving the needle. To break through, you
      need to <strong>grow your business online nationally</strong> — but the playbook is
      completely different from the one that worked locally. Here's what actually works in
      2026, across Google, ChatGPT and the entire AI-answer ecosystem.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="what-national-means">What "growing nationally online" really means in 2026</h2>
    <p>
      A decade ago it meant ranking on Google for high-volume, non-geo keywords ("best
      accountant," "buy shoes online"). Today it means being the answer{" "}
      <em>everywhere your buyer looks</em>: Google search, Google AI Overviews, ChatGPT,
      Perplexity, YouTube, Reddit, and the podcast in their AirPods. National growth is now a
      multi-surface problem.
    </p>
    <p>
      Local-only playbooks (Google Business Profile optimization, local citation building,
      neighborhood SEO) don't extend cleanly to national — they were designed to win a 25-mile
      radius. You need a different stack.
    </p>

    <h2 id="three-layers">The 3 layers of online growth at national scale</h2>
    <div className="my-6 overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Layer</th>
            <th className="p-3">What it does</th>
            <th className="p-3">Primary channel</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["SEO", "Ranks your pages on Google & Bing for the queries buyers type", "Organic search results (blue links + featured snippets)"],
            ["AEO", "Formats your pages so AI engines can lift a clean answer", "ChatGPT Search, Perplexity, Google AI Overviews"],
            ["GEO", "Makes your brand entity recognizable across the whole web", "AI citations, unlinked mentions, entity graphs"],
          ].map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`p-3 ${i === 0 ? "font-display font-bold text-[#F09819]" : "text-white/80"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p>
      You need all three. SEO drives clicks. AEO drives citations. GEO makes you the default
      brand in your category — the one AI engines name-drop before all others.
    </p>

    <Callout kind="info">
      <strong>Reality check:</strong> National online growth is a compounding game. Founders
      who ship consistent, structured content weekly outperform those who spend 5x more on ads
      by month 6. Every week you don't publish, a competitor is being indexed instead.
    </Callout>

    <h2 id="why-local-fails">Why local playbooks fail when you go national</h2>
    <ul>
      <li><strong>Google Business Profile is capped.</strong> It boosts you inside a geographic radius, not across states.</li>
      <li><strong>Local citations don't scale.</strong> BBB, Yelp, and city directories don't help you rank in cities you're not in.</li>
      <li><strong>National keywords are 10–100x more competitive.</strong> You're up against SaaS companies, DTC brands, and content sites with 5+ years of authority.</li>
      <li><strong>Word-of-mouth doesn't cross state lines.</strong> Referrals dry up outside your existing network.</li>
      <li><strong>ChatGPT and AI Overviews became the new discovery layer.</strong> If they don't cite you, you don't exist to remote buyers.</li>
    </ul>

    <h2 id="seven-steps">The 7-step playbook to grow your business online nationally</h2>

    <h3>Step 1 — Nail your positioning and category</h3>
    <p>
      National growth requires a crisp answer to <em>"what category am I the default choice
      for?"</em> Vague positioning ("we help small businesses") loses. Specific positioning
      ("AI SEO autopilot for solo founders") wins. Write it, test it against 5 prospects, and
      commit.
    </p>

    <h3>Step 2 — Build a national-scale content engine</h3>
    <p>
      One well-structured pillar article per week, plus 2–4 supporting posts, becomes 100+
      indexed URLs in a year. Publish on your own domain, not a Medium account. Use topic
      clusters (one pillar + 5–10 supporting posts per topic). This is the highest-ROI
      channel in 2026 — but only if you're consistent.
    </p>

    <h3>Step 3 — Ship AEO- and GEO-ready structure on every page</h3>
    <ol>
      <li>Question-style H2s (that match how buyers actually search)</li>
      <li>50-word answer blocks under each question (extractable by ChatGPT and AI Overviews)</li>
      <li><code>FAQPage</code> + <code>HowTo</code> + <code>Article</code> JSON-LD schema</li>
      <li><code>Organization</code> + <code>Person</code> JSON-LD with <code>sameAs</code> links (LinkedIn, X, Wikipedia, Crunchbase)</li>
      <li><code>/llms.txt</code> at your domain root — the AI-era sitemap most sites still don't have</li>
    </ol>

    <h3>Step 4 — Allow AI crawlers in robots.txt</h3>
    <p>
      Explicitly allow <code>GPTBot</code>, <code>OAI-SearchBot</code>, <code>Google-Extended</code>,
      <code>PerplexityBot</code>, <code>Anthropic-AI</code>, and <code>CCBot</code>. Then
      submit your sitemap to <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer">Bing Webmaster Tools</a> —
      Bing is the source for ~85% of ChatGPT's citations, and most brands never submit.
    </p>

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want steps 2–4 running on autopilot?",
        sub: "Soro researches, writes, schemas and publishes daily national-scale content — while you build the business.",
        cta: "Try Soro free",
      }}
    />

    <h3>Step 5 — Earn brand mentions at national scale</h3>
    <p>
      Backlinks still matter for Google. <strong>Unlinked brand mentions</strong> now matter
      just as much for ChatGPT and Perplexity. Build a monthly rhythm across:
    </p>
    <ul>
      <li><strong>Podcasts</strong> — pitch 4 shows/month in your niche. Get named in show notes and transcripts.</li>
      <li><strong>Reddit</strong> — post genuinely useful answers in 3 subreddits/month, not spam.</li>
      <li><strong>Substack + newsletters</strong> — write one 800-word guest essay/month for a newsletter in your space.</li>
      <li><strong>YouTube</strong> — get named in creator videos in your category. Reach out with a specific value trade.</li>
      <li><strong>Directories</strong> — G2, Product Hunt, AlternativeTo, Capterra, industry-specific lists.</li>
    </ul>

    <h3>Step 6 — Turn on paid channels once organic is compounding</h3>
    <p>
      Ads should <em>amplify</em> a working organic engine, not replace one. Start with:
    </p>
    <ul>
      <li>Google Search ads on brand terms + high-intent long-tail (defensive + high-ROI)</li>
      <li>LinkedIn ads for B2B — targeting job titles you already convert on</li>
      <li>YouTube TrueView for awareness once you have organic proof it converts</li>
    </ul>
    <p>
      Skip broad Meta / TikTok ads for national B2B growth — the intent signal is too weak
      unless you have a genuinely viral offer.
    </p>

    <h3>Step 7 — Measure the right leading indicators</h3>
    <p>
      Revenue is a lagging indicator. Track these leading indicators monthly:
    </p>
    <ol>
      <li><strong>Google Search Console impressions</strong> across all pages (proxy for topical authority)</li>
      <li><strong>Branded search volume</strong> in GSC — Google searches for your brand name (best signal of national awareness)</li>
      <li><strong>ChatGPT / Perplexity citation share</strong> — 10 category prompts, monthly, log who's cited</li>
      <li><strong>Direct traffic percentage</strong> — climbing = brand is compounding</li>
      <li><strong>Number of unlinked brand mentions</strong> across Reddit, Substack, podcast transcripts</li>
    </ol>

    <h2 id="tactics-by-channel">The 2026 national growth channel mix</h2>
    <div className="my-6 overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Channel</th>
            <th className="p-3">Time to ROI</th>
            <th className="p-3">Fit for national scale</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["Organic SEO + AEO content", "3–6 months", "★★★★★ — compounds forever"],
            ["ChatGPT / Perplexity citations", "2–8 weeks", "★★★★★ — new & underrated"],
            ["Podcast guesting", "6–12 weeks", "★★★★ — great for personal brand"],
            ["Reddit + community", "6–12 weeks", "★★★★ — trust signal + AI mentions"],
            ["YouTube (own channel)", "6–18 months", "★★★ — high leverage but slow"],
            ["Google Search ads", "Immediate", "★★★★ — best for brand + high-intent"],
            ["LinkedIn ads (B2B)", "1–3 months", "★★★ — narrow, expensive, high-quality"],
            ["Meta / TikTok ads", "Immediate", "★★ — usually not the answer for national B2B"],
            ["Cold email outreach", "1–6 weeks", "★★★ — legally fragile, quickly saturating"],
          ].map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`p-3 ${i === 0 ? "font-display font-bold text-white" : i === 2 ? "text-[#F09819]" : "text-white/80"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 id="mistakes">4 mistakes that stall national growth</h2>
    <ol>
      <li><strong>Chasing every channel at once.</strong> Pick 2–3, run them for 6 months, then add more.</li>
      <li><strong>Publishing without schema.</strong> Content without <code>FAQPage</code>/<code>HowTo</code> markup is invisible on AI search.</li>
      <li><strong>Ignoring Bing.</strong> Bing is the pipe to ChatGPT. Submit your sitemap today.</li>
      <li><strong>Vague brand positioning.</strong> "We help everyone with everything" is a scaling ceiling — pick a category and win it first.</li>
    </ol>

    <FaqBlock faq={post.faq} />

    <h2 id="next">Your next move</h2>
    <p>
      National online growth compounds. Every week you don't ship, a competitor's page gets
      indexed instead of yours. If you want a hands-off way to run the AEO/GEO layer of this
      playbook,{" "}
      <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>Soro</a>{" "}
      publishes daily, schema-injected articles straight to your CMS — the same content engine
      we used to write this post.
    </p>
    <p>
      Related reads:{" "}
      <a href="/blog/how-to-rank-on-ai-search-engines">how to rank on AI search engines</a>,{" "}
      <a href="/blog/geo-vs-seo">GEO vs SEO</a>, and{" "}
      <a href="/blog/how-to-get-cited-by-chatgpt">how to get cited by ChatGPT</a>.
    </p>
  </>
);
